import * as React from "react";
import {useEffect, useState} from "react";
import TextFieldValidator from "../form/TextFieldValidator";
import Schedule, {Frequency} from "../../model/Schedule";
import {Box, useTheme} from "@mui/material";
import FormGroupSpacer from "../form/FormGroupSpacer";
import {DateAdapter} from "@rschedule/core";
import Weekday = DateAdapter.Weekday;

export interface DayOfWeek {
  weekday: Weekday
  label: string
}

export interface DayOfMonth {
  day: number
  label: string
}

export interface MonthOfYear {
  month: number
  label: string
}

export const DayOfWeekOptions: DayOfWeek[] = [
  {weekday: "SU", label: "Sun" },
  {weekday: "MO", label: "Mon" },
  {weekday: "TU", label: "Tue" },
  {weekday: "WE", label: "Wed" },
  {weekday: "TH", label: "Thu" },
  {weekday: "FR", label: "Fri" },
  {weekday: "SA", label: "Sat" },
]

export const DayOfMonthOptions: DayOfMonth[] = [
  {day: 1, label: "1st"},
  {day: 2, label: "2nd"},
  {day: 3, label: "3rd"},
  {day: 4, label: "4th"},
  {day: 5, label: "5th"},
  {day: 6, label: "6th"},
  {day: 7, label: "7th"},
  {day: 8, label: "8th"},
  {day: 9, label: "9th"},
  {day: 10, label: "10th"},
  {day: 11, label: "11th"},
  {day: 12, label: "12th"},
  {day: 13, label: "13th"},
  {day: 14, label: "14th"},
  {day: 15, label: "15th"},
  {day: 16, label: "16th"},
  {day: 17, label: "17th"},
  {day: 18, label: "18th"},
  {day: 19, label: "19th"},
  {day: 20, label: "20th"},
  {day: 21, label: "21st"},
  {day: 22, label: "22nd"},
  {day: 23, label: "23rd"},
  {day: 24, label: "24th"},
  {day: 25, label: "25th"},
  {day: 26, label: "26th"},
  {day: 27, label: "27th"},
  {day: 28, label: "28th"},
  {day: 29, label: "29th"},
  {day: 30, label: "30th"},
  {day: 31, label: "31st"},
  {day: -1, label: "Last day"},
]

export const MonthOfYearOptions: MonthOfYear[] = [
  {month: 1, label: "Jan"},
  {month: 2, label: "Feb"},
  {month: 3, label: "Mar"},
  {month: 4, label: "Apr"},
  {month: 5, label: "May"},
  {month: 6, label: "Jun"},
  {month: 7, label: "Jul"},
  {month: 8, label: "Aug"},
  {month: 9, label: "Sep"},
  {month: 10, label: "Oct"},
  {month: 11, label: "Nov"},
  {month: 12, label: "Dec"}
]

const ScheduleSelector = ({
  value,
  startDate,
  label,
  disabled,
  onChange,
  formvalidator
  }: {
  value: Schedule
  startDate?: Date
  label?: string
  disabled?: boolean
  onChange?(value: Schedule): any
  formvalidator?: any
}) => {
  const defaultOptions: Schedule[] = [
    new Schedule({name: "Once", frequency: Frequency.Once, interval: 1}),
    new Schedule({name: "Weekly", frequency: Frequency.Weekly, interval: 1}),
    new Schedule({name: "Bi-weekly", frequency: Frequency.Weekly, interval: 2}),
    // TODO: new Schedule({name: "Semi-Monthly", frequency: Frequency.Monthly, interval: 1}),
    new Schedule({name: "Monthly", frequency: Frequency.Monthly, interval: 1, on: [-1]}),
    new Schedule({name: "Semi-Monthly", frequency: Frequency.Monthly, interval: 1, on: [1,15]}),
    new Schedule({name: "Quarterly", frequency: Frequency.Monthly, interval: 3}),
    // new Schedule({name: "Semi-Annual", frequency: Frequency.Monthly, interval: 6}),
    new Schedule({name: "Yearly", frequency: Frequency.Yearly, interval: 1, on: [-1]}),
    new Schedule({name: "Custom"})
  ]
  
  const [schedule, setSchedule] = useState<Schedule>(value)
  const [frequency, setFrequency] = useState<Frequency>(Frequency.Once)
  const [interval, setInterval] = useState<string>("")
  const [dayOfWeekOption, setDayOfWeekOption] = useState<DayOfWeek>(DayOfWeekOptions[0])
  const [dayOfMonthOption, setDayOfMonthOption] = useState<DayOfMonth>(DayOfMonthOptions[DayOfMonthOptions.length-1])
  const [monthOfYearOptions, setMonthOfYearOptions] = useState<MonthOfYear[]>([])
  const [options, setOptions] = useState<Schedule[]>(defaultOptions)

  useEffect( () => {
    if (!value) {
      return
    }
    setSchedule(value)
    setFrequency(value.frequency)
    setInterval(value.interval.toString())
    if (value.frequency === Frequency.Weekly) {
      let index = 0
      if (value.on && value.on.length > 0) {
        const weekday = value.on[0]
        index = DayOfWeekOptions.findIndex((option: DayOfWeek) => option.weekday === weekday)
      } else if (startDate) {
        index = startDate.getDay()
      }

      if (index >= 0) {
        setDayOfWeekOption(DayOfWeekOptions[index])
      } else {
        setDayOfWeekOption(DayOfWeekOptions[0])
      }
    } else if (value.frequency === Frequency.Monthly) {
      let index = 0
      if (value.on && value.on.length > 0) {
        const day = value.on[0]
        index = DayOfMonthOptions.findIndex((option: DayOfMonth) => option.day === day)
      } else if (startDate) {
        index = startDate.getDate() - 1
      }

      if (index >= 0) {
        setDayOfMonthOption(DayOfMonthOptions[index])
      } else {
        setDayOfMonthOption(DayOfMonthOptions[DayOfMonthOptions.length-1])
      }
    } else if (value.frequency === Frequency.Yearly) {
      const monthOptions: MonthOfYear[] = []
      if (value.each && value.each.length > 0) {
        value.each.forEach((month: number) => {
          if (month >= 1 && month <= 12) {
            monthOptions.push(MonthOfYearOptions[month-1])
          }
        })
      } else if (startDate) {
        monthOptions.push(MonthOfYearOptions[startDate.getMonth()])
      }
      monthOptions.sort((a: MonthOfYear, b: MonthOfYear) => a.month - b.month)
      setMonthOfYearOptions(monthOptions)

      let index = 0
      if (value.on && value.on.length > 0) {
        const day = value.on[0]
        index = DayOfMonthOptions.findIndex((option: DayOfMonth) => option.day === day)
      } else if (startDate) {
        index = startDate.getDate() - 1
      }

      if (index >= 0) {
        setDayOfMonthOption(DayOfMonthOptions[index])
      } else {
        setDayOfMonthOption(DayOfMonthOptions[DayOfMonthOptions.length-1])
      }
    }
    // Update custom option to match value schedule
    const customOption = defaultOptions[defaultOptions.length-1]
    customOption.frequency = value.frequency
    customOption.interval = value.interval
    if (customOption.frequency === Frequency.Monthly || customOption.frequency === Frequency.Yearly) {
      customOption.on = [-1]
    }
    setOptions(defaultOptions)
  }, [value, startDate])

  const theme = useTheme()

  const styles = {
    customDetails: {
      backgroundColor: theme.palette.grey["300"]
    },
    chip: {
      color: theme.palette.grey["500"]
    }
  }

  if (!schedule) {
    return null
  }

  return(
    <React.Fragment>
      <TextFieldValidator
        type="text"
        validators={{ required: true }}
        required
        name="repeats"
        label={label ?? "Repeats"}
        variant="standard"
        disabled={disabled}
        fullWidth
        helperText={value && value.name !== "Custom" ? schedule.getDescription(undefined, true) : undefined}
        autocompleteOptions={{
          freeSolo: false,
          options: options,
          value: schedule,
          disabled: disabled,
          disableClearable: true,
          getOptionLabel: (option: Schedule) => {
            return option?.name ?? ""
           },
          isOptionEqualToValue: (option: Schedule, value: Schedule) =>
          {
            return option?.name === value?.name
          },
          onChange: (event: any, value: Schedule, reason: any) => {
            setSchedule(value)
            if (onChange) {
              onChange(value)
            }
          }
        }}
      />
      {value && value.name === "Custom" &&
        <Box display="flex" flexDirection="row" justifyContent="stretch" sx={styles.customDetails}>
          <TextFieldValidator
            type="number"
            validators={{ minValue: 1, isInt: true }}
            name="interval"
            label="Every"
            variant="standard"
            margin="dense"
            styleProp={{width:120}}
            value={interval}
            onChange={(event: any) => {
              setInterval(event.target.value)
              schedule.interval = parseInt(event.target.value)
              if (onChange) {
                onChange(schedule)
              }
            }}
          />
          <FormGroupSpacer/>
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            styleProp={{width:120}}
            name="frequency"
            label="Frequency"
            variant="standard"
            margin="dense"
            autocompleteOptions={{
              freeSolo: false,
              options: Object.values(Frequency),
              value: frequency,
              disableClearable: true,
              getOptionLabel: (option: Frequency) => {
                switch (option) {
                  case Frequency.Once:
                    return "Once"
                  case Frequency.Weekly:
                    return "Week(s)"
                  case Frequency.Monthly:
                    return "Month(s)"
                  case Frequency.Yearly:
                    return "Year(s)"
                }
              },
              onChange: (event: any, value: Frequency, reason: any) => {
                setFrequency(value)
                schedule.frequency = value
                switch (value) {
                  case Frequency.Once:
                    schedule.on = undefined
                    schedule.each = undefined
                    break
                  case Frequency.Weekly:
                    setDayOfWeekOption(DayOfWeekOptions[0])
                    schedule.on = [DayOfWeekOptions[0].weekday]
                    schedule.each = undefined
                    break
                  case Frequency.Monthly:
                    setDayOfMonthOption(DayOfMonthOptions[DayOfMonthOptions.length-1])
                    schedule.on = [-1]
                    schedule.each = undefined
                    break
                  case Frequency.Yearly:
                    setDayOfMonthOption(DayOfMonthOptions[DayOfMonthOptions.length-1])
                    setMonthOfYearOptions(startDate ? [MonthOfYearOptions[startDate.getMonth()]] : [])
                    schedule.on = [-1]
                    schedule.each = startDate ? [MonthOfYearOptions[startDate.getMonth()].month] : undefined
                    break
                }
                if (onChange) {
                  onChange(schedule)
                }
              }
            }}
          />
          <FormGroupSpacer/>
          {frequency === Frequency.Weekly &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              styleProp={{width:120}}
              name="dayOfWeek"
              label="On"
              variant="standard"
              margin="dense"
              autocompleteOptions={{
                freeSolo: false,
                options: DayOfWeekOptions,
                value: dayOfWeekOption,
                disableClearable: true,
                getOptionLabel: (option: DayOfWeek) => option.label ?? "",
                onChange: (event: any, value: DayOfWeek, reason: any) => {
                  setDayOfWeekOption(value)
                  schedule.on = [value.weekday]
                  if (onChange) {
                    onChange(schedule)
                  }
                }
              }}
            />
          }
          {(frequency === Frequency.Monthly || frequency === Frequency.Yearly) &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              styleProp={{width:120}}
              name="dayOfMonth"
              label="On the"
              variant="standard"
              margin="dense"
              autocompleteOptions={{
                freeSolo: false,
                options: DayOfMonthOptions,
                value: dayOfMonthOption,
                disableClearable: true,
                getOptionLabel: (option: DayOfMonth) => option.label ?? "",
                onChange: (event: any, value: DayOfMonth, reason: any) => {
                  setDayOfMonthOption(value)
                  schedule.on = [value.day]
                  if (onChange) {
                    onChange(schedule)
                  }
                }
              }}
            />
          }
        </Box>
      }
      {(value.name === "Custom" && frequency === Frequency.Yearly) &&
        <Box display="flex" flexGrow={1} width="100%" sx={styles.customDetails}>
          <TextFieldValidator
            type="text"
            validators={{ required: false }}
            required
            name="monthsOfYear"
            label="Of each"
            variant="standard"
            margin="dense"
            fullWidth
            styleProp={{width:"100%"}}
            autocompleteOptions={{
              freeSolo: false,
              multiple: true,
              options: MonthOfYearOptions,
              value: monthOfYearOptions,
              disableClearable: false,
              // filterSelectedOptions: true,
              ChipProps: {color: "info", size: "small"},
              sx: {width: "100%"},
              // sx:{
              //   "& + .MuiAutocomplete-popper .MuiAutocomplete-option": {
              //     backgroundColor: "#363636",
              //   },
              //     "& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected='true']":
              //   {
              //     backgroundColor: "#4396e6",
              //   },
              //     "& + .MuiAutocomplete-popper .MuiAutocomplete-option[aria-selected ='true'].Mui-focused":
              //   {
              //     backgroundColor: "#3878b4",
              //   }
              // },
              // getOptionLabel: (option: MonthOfYear) => option && option.label ? option.label : "",
              isOptionEqualToValue: (option: MonthOfYear, value: MonthOfYear) => option.month === value.month,
              onChange: (event: any, value: MonthOfYear[], reason: any) => {
                setMonthOfYearOptions(value)
                schedule.each = value.map((option: MonthOfYear) => option.month)
                if (onChange) {
                  onChange(schedule)
                }
              }
            }}
          />
        </Box>
      }
    </React.Fragment>
  )
}

export default ScheduleSelector

